import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import {
  type BlueprintQuestion,
  type StudentFieldKey,
  type StudentFieldValue,
  type StudentRow,
} from '@backrs_ui/grow-ui/src/redux/coachingAnalytics';

import {
  alignmentLabel,
  ayotLevelLabel,
  ayotTrackLabel,
  bookmarkTypeLabel,
  careerTierLabel,
  completionStatusLabel,
  humanize,
  profileSignalLabel,
  programLabel,
  readinessBandLabel,
  recencyLabel,
  toolLabel,
  unlockStatusLabel,
} from './analytics.labels';

const { viewData } = appStrings.modules.coachingAnalytics;
const { columns: headers } = viewData;

/** One table column: which field to request and how to print its value. */
export type StudentColumn = Readonly<{
  id: string;
  header: string;
  field: string;
  format: (value: StudentFieldValue, row: StudentRow) => string;
  /** Renders the formatted value inside a count pill. */
  pill?: boolean;
}>;

const isList = (value: StudentFieldValue): value is readonly string[] => Array.isArray(value);

const text = (value: StudentFieldValue): string => {
  if (value == null) return '';
  if (isList(value)) return value.join(', ');
  if (typeof value === 'object') return '';

  return String(value);
};

const labelled =
  (label: (id: string) => string) =>
  (value: StudentFieldValue): string => {
    if (value == null) return '';
    if (isList(value)) return value.map(label).join(', ');

    return typeof value === 'string' ? label(value) : text(value);
  };

const yesNo = (value: StudentFieldValue): string => {
  if (value == null) return '';

  return value ? viewData.yes : viewData.no;
};

const percent = (value: StudentFieldValue): string =>
  typeof value === 'number' ? `${Math.round(value)}%` : '';

const column = (
  id: string,
  header: string,
  format: StudentColumn['format'] = text,
  field: string = id,
): StudentColumn => ({ id, header, field, format });

export const PHOTO_FIELD = 'profile_image_url';

/** Exported as two columns; the table shows them as one photo + full-name cell. */
const NAME_COLUMNS: readonly StudentColumn[] = [
  column('first_name', headers.firstName),
  column('last_name', headers.lastName),
];

export const COMMON_COLUMNS: readonly StudentColumn[] = [
  ...NAME_COLUMNS,
  column('institution', headers.institution),
  column('grade', headers.grade),
  column('program', headers.program, labelled(programLabel)),
  column('enrollment_type', headers.enrollment),
];

/** The list field a column can be ordered by; map-entry columns and the merged Name cell differ. */
export const sortFieldOf = (col: StudentColumn): string | undefined => {
  if (col.id === 'name') return 'last_name';

  return col.id === col.field ? col.field : undefined;
};

/** Columns as rendered in the table: name columns collapse into a single Name column. */
export const displayColumns = (columns: readonly StudentColumn[]): readonly StudentColumn[] =>
  columns.some((col) => col.id === 'first_name')
    ? [
        column('name', headers.name),
        ...columns.filter((col) => col.id !== 'first_name' && col.id !== 'last_name'),
      ]
    : columns;

export const COLUMNS = {
  blueprintCompleted: column('blueprint_completed', headers.blueprintCompleted, yesNo),
  blueprintCompletedAt: column('blueprint_completed_at', headers.blueprintCompletedAt),
  ayotCompleted: column('ayot_completed', headers.ayotCompleted, yesNo),
  ayotCompletedAt: column('ayot_completed_at', headers.ayotCompletedAt),
  lifeReadinessStatus: column(
    'life_readiness_status',
    headers.lifeReadinessStatus,
    labelled(completionStatusLabel),
  ),
  lifeReadinessPercent: column('life_readiness_percent', headers.lifeReadinessPercent, percent),
  careerTrack: column('career_track', headers.careerTrack, labelled(careerTierLabel)),
  entrepreneurship: column('entrepreneurship_recommended', headers.entrepreneurship, yesNo),
  assessedTrack: column('assessed_track', headers.assessedTrack, labelled(humanize)),
  assessmentGrade: column('assessment_grade', headers.assessmentGrade),
  trackAlignment: column('track_alignment', headers.trackAlignment, labelled(alignmentLabel)),
  profileStrength: column('profile_strength', headers.profileStrength, labelled(unlockStatusLabel)),
  gpa: column('assessed_gpa', headers.gpa),
  sat: column('assessed_sat_score', headers.sat),
  act: column('assessed_act_score', headers.act),
  courseworkRigor: column('coursework_rigor', headers.courseworkRigor),
  majorRigor: column('major_rigor', headers.majorRigor, labelled(humanize)),
  schoolSelectivity: column('school_selectivity', headers.schoolSelectivity, labelled(humanize)),
  lastLogin: column('last_login_at', headers.lastLogin),
  loginRecency: column('login_recency', headers.loginRecency, labelled(recencyLabel)),
  toolsUsed: column('tools_used', headers.toolsUsed, labelled(toolLabel)),
  toolSessions: column('tool_sessions', headers.toolSessions),
  lastToolSession: column('last_tool_session_at', headers.lastToolSession),
  networks: column('networks', headers.networks),
  coach: column('coach', headers.coach),
  coachType: column('coach_type', headers.coachType, labelled(humanize)),
  profileFacts: column('profile_signals', headers.profileFacts, labelled(profileSignalLabel)),
  bookmarkTypes: column('bookmark_types', headers.bookmarkTypes, labelled(bookmarkTypeLabel)),
  bookmarks: column('bookmarks', headers.bookmarks),
} as const;

/** Activity counts, headed with the chart's month window so the time frame is explicit. */
export const activityColumns = (months: number): readonly StudentColumn[] => [
  column('posts', headers.postsInWindow(months)),
  column('goals', headers.goalsInWindow(months)),
  column('comments', headers.commentsInWindow(months)),
];

export const singleCoachColumn = (name: string): StudentColumn =>
  column('coach', headers.coach, (value) =>
    isList(value) ? value.filter((coach) => coach === name).join(', ') : text(value),
  );

/** Questions answered out of the questions a student can see. */
export const lifeReadinessAnsweredColumn = (visibleQuestions: number): StudentColumn =>
  column('life_readiness_answered', headers.lifeReadinessAnswered, (value) =>
    typeof value === 'number' ? viewData.answeredOf(value, visibleQuestions) : '',
  );

const mapEntry = (value: StudentFieldValue, key: string): StudentFieldValue =>
  value != null && typeof value === 'object' && !isList(value) ? (value[key] ?? null) : null;

/** One column per key of a map-kind field, labelled with the key's name. */
const mapColumns = (
  field: string,
  keys: readonly StudentFieldKey[],
  label: (id: string) => string,
): readonly StudentColumn[] =>
  keys.map((entry) =>
    column(
      `${field}.${entry.key}`,
      entry.name,
      (value) => labelled(label)(mapEntry(value, entry.key)),
      field,
    ),
  );

export const readinessBandColumns = (keys: readonly StudentFieldKey[]) =>
  mapColumns('life_readiness_bands', keys, readinessBandLabel);

export const readinessFacetColumns = (keys: readonly StudentFieldKey[]) =>
  mapColumns(
    'life_readiness_facets',
    keys.map((entry) => ({
      ...entry,
      name: appStrings.modules.coachingAnalytics.lifeReadiness.byFacet.columnHeader(entry.name),
    })),
    readinessBandLabel,
  );

export const ayotDimensionLevelColumns = (keys: readonly StudentFieldKey[]) =>
  mapColumns(
    'ayot_dimension_levels',
    keys.map((entry) => ({
      ...entry,
      name: appStrings.modules.coachingAnalytics.onTrack.heatMap.columnHeader(entry.name),
    })),
    ayotLevelLabel,
  );

export const ayotAcademicBandColumns = (keys: readonly StudentFieldKey[]) =>
  mapColumns(
    'ayot_academic_bands',
    keys.map((entry) => ({
      ...entry,
      name: appStrings.modules.coachingAnalytics.onTrack.heatMap.measureColumnHeader(entry.name),
    })),
    ayotTrackLabel,
  );

const FIT_LABELS: Readonly<Record<string, string>> = {
  strong: appStrings.modules.coachingAnalytics.blueprint.careerPathFit.strong,
  medium: appStrings.modules.coachingAnalytics.blueprint.careerPathFit.medium,
  low: appStrings.modules.coachingAnalytics.blueprint.careerPathFit.low,
};

export const careerPathFitColumns = (keys: readonly StudentFieldKey[]) =>
  mapColumns('career_path_fit', keys, (id) => FIT_LABELS[id] ?? humanize(id)).map((col) => ({
    ...col,
    pill: true,
  }));

/** The options a student chose on one Blueprint question, printed as their answer text. */
export const blueprintAnswerColumn = (question: BlueprintQuestion): StudentColumn => {
  const answers = new Map(question.options.map((option) => [String(option.id), option.answer]));

  return column(
    `blueprint_answers.${question.id}`,
    appStrings.modules.coachingAnalytics.blueprint.answers.columnHeader(question.title),
    (value) => labelled((id) => answers.get(id) ?? id)(mapEntry(value, String(question.id))),
    'blueprint_answers',
  );
};

export const unlockStatusColumns = (keys: readonly StudentFieldKey[]) =>
  mapColumns('unlock_status_by_category', keys, unlockStatusLabel);

export const fieldsFor = (columns: readonly StudentColumn[]): readonly string[] =>
  columns.map((col) => col.field).filter((field, index, all) => all.indexOf(field) === index);

export const cellsFor = (columns: readonly StudentColumn[], row: StudentRow): string[] =>
  columns.map((col) => col.format(row[col.field] ?? null, row));

const escapeCsv = (cell: string): string =>
  /[",\n\r]/.test(cell) ? `"${cell.replace(/"/g, '""')}"` : cell;

export const toTsv = (columns: readonly StudentColumn[], rows: readonly StudentRow[]): string =>
  [columns.map((col) => col.header), ...rows.map((row) => cellsFor(columns, row))]
    .map((cells) => cells.map((cell) => cell.replace(/[\t\n\r]+/g, ' ')).join('\t'))
    .join('\n');

export const toCsv = (columns: readonly StudentColumn[], rows: readonly StudentRow[]): string =>
  [columns.map((col) => col.header), ...rows.map((row) => cellsFor(columns, row))]
    .map((cells) => cells.map(escapeCsv).join(','))
    .join('\r\n');
